import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import GenericCard from '@ui-components/Card/GenericCard';
import { GenericTag } from '@ui-components/Tag/GenericTag';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ReactComponent as IconCheck } from '@shared/assets/Icons/IconCheckSquared.svg';
import { ReactComponent as IconCancel } from '@shared/assets/Icons/IconCrossSquare.svg';
import { EditableTagList } from 'kontrolEdge/components/Shared/NameTagEdit/EditableTagList';
import { useAxiosAuth0 } from '@shared/config/useAxiosAuth0';
import { SkeletonBlock } from '@ui-components/Skeleton/GenericSkeleton';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store/store';
import {
	setBackboneVrfs,
	clearBackboneVrfs,
	setVrfFilterSelection,
	setBackboneConnectorTypes,
	setBackboneBgpStatusCounts,
} from '@shared/redux/slices/kontrolEdge/backboneDetailsSlice';
import { useBackboneDetails } from './useBackboneDetails';
import { readCachedFetch, writeCachedFetch } from '@hooks/useCachedFetch';
import {
	isVrfFilterUnfiltered,
	BACKBONE_CONNECTOR_TYPE_GROUPS,
	type BackboneConnectorTypeGroup,
	connectedServiceTypeToGroup,
	matchesConnectorTypeGroupSelection,
	connectorIsEstablishedToBgpStatusFilterId,
	matchesBgpStatusFilterSelection,
} from './BackboneDetailsData';
import {
	GLOBAL_BW,
	fmtBw,
	formatBandwidthFromMbpsInline,
	roundBw,
} from '../backbone.data';
import { DataStateView } from './StateViews';
import ConnectorsTab from './Connectors/ConnectorsTab';
import {
	bbConnectorServiceSubscriptionId,
	fetchBbConnectorList,
	fetchBbaasBgpSessions,
	mapBbaasBgpSessionToConnector,
	updateBbaas,
	updateVrfDisplayName,
	type BbConnectorListItem,
} from '../requests/request';
import ConnectorsModal from './Connectors/ConnectorsModal';
import TopologyTab from './Topology/TopologyTab';
import BGPTab from './BGP/BGPTab';
import RoutingTab from './Routing/RoutingTab';
import PolicyRulesTab from './PolicyRulesTab';

/** Spinner inside the backbone name input while saving */
const BackboneNameInputInlineLoader = () => (
	<span
		className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center"
		aria-hidden
	>
		<span className="block h-4 w-4 rounded-full border-2 border-semantic-border-primary border-t-semantic-text-brand animate-spin" />
	</span>
);

/** Spinner inside the VRF display name input while saving */
const VrfHeaderInputInlineLoader = () => (
	<span
		className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center"
		aria-hidden
	>
		<span className="block h-4 w-4 rounded-full border-2 border-semantic-border-primary border-t-semantic-text-brand animate-spin" />
	</span>
);

type BackboneBgpSession = ReturnType<typeof mapBbaasBgpSessionToConnector>;

/** Label for summary popovers (subscription id from detail, then legacy fallbacks). */
function connectorSummaryListLabel(c: BbConnectorListItem): string {
	const d = c.connectedServiceDetail;
	if (d && typeof d === 'object' && d.subscriptionId != null) {
		const s = String(d.subscriptionId).trim();
		if (s !== '') return s;
	}
	const fallback = bbConnectorServiceSubscriptionId(c);
	if (fallback !== '') return fallback;
	return c.name?.trim() || `Connector ${c.id}`;
}

const BackboneDetails: React.FC = () => {
	const [searchParams] = useSearchParams();
	const backboneId = searchParams.get('backboneId');
	const navigate = useNavigate();
	const api = useAxiosAuth0();
	const [refresh, setRefresh] = useState(0);
	const selectionTenantId = useAppSelector((state: RootState) => state.selection.tenantId);
	const tenantId = selectionTenantId ?? null;

	const { raw: bb, loading, error: backboneError } = useBackboneDetails(
		backboneId,
		api,
		refresh,
		tenantId
	);

	// Tenant the resource actually lives in, sourced from the backbone row
	// itself (NOT the SideNav lens). Forwarded to the Tags API so writes
	// hit the right scope regardless of the active lens — the backend's
	// resource-side adapter is strict on `(id, tenantId)` and doesn't honor
	// the partner→child hierarchy. See PortDetails.tsx for the full
	// rationale. Falls back to the lens tenant when `bb` doesn't expose
	// `tenantId` yet (during initial load) so existing customer-view flows
	// keep working.
	const resourceTenantId = useMemo<number | undefined>(() => {
		const t = (bb as { tenantId?: unknown } | null | undefined)?.tenantId;
		if (typeof t === "number" && Number.isFinite(t)) return t;
		return tenantId ?? undefined;
	}, [bb, tenantId]);

	// Rich tags hydrated inline by the BBAAS list endpoint (§5.5 — the
	// backend calls `attachTagsToRows({ resourceType: 'KE_BBAAS' })` in
	// `list-bbaas/helpers.js`). Seeding the modal with these skips the
	// §5.1 round-trip and renders the chip strip on first paint. Returns
	// `undefined` (NOT `[]`) when the field isn't shipped yet so the hook
	// falls back to its own fetch instead of locking in an empty seed.
	const initialAssignedTags = useMemo(
		() => (bb?.tagsRich != null && bb.tagsRich.length > 0 ? bb.tagsRich : undefined),
		[bb]
	);

	const [isEditingName, setIsEditingName] = useState(false);
	const [draftBackboneName, setDraftBackboneName] = useState('');
	const [editedBackboneName, setEditedBackboneName] = useState<string | null>(null);

	const [backboneTags, setBackboneTags] = useState<string[]>([]);
	const [savingBackboneName, setSavingBackboneName] = useState(false);
	const [savingBackboneTags, setSavingBackboneTags] = useState(false);
	const [backboneMutationError, setBackboneMutationError] = useState<string | null>(null);

	const [isEditingVrfFilter, setIsEditingVrfFilter] = useState(false);
	const [draftVrfDisplayName, setDraftVrfDisplayName] = useState('');
	const [savingVrfName, setSavingVrfName] = useState(false);

	// The last connector list and BGP sessions for this backbone paint at once when the
	// user comes back (a view switch, Back); the requests below still run and replace them.
	// The connector list is tenant-wide, but keying it per backbone keeps the rule simple:
	// a key always names the resource and the tenant it was fetched with.
	const connectorsCacheKey =
		backboneId && tenantId != null
			? `kontroledge/bbaas/${backboneId}/bb-connector?tenantId=${tenantId}`
			: null;
	const bgpSessionsCacheKey =
		backboneId && tenantId != null
			? `kontroledge/bbaas/${backboneId}/bgp-sessions?tenantId=${tenantId}`
			: null;
	const [connectorList, setConnectorList] = useState<BbConnectorListItem[]>(
		() => readCachedFetch<BbConnectorListItem[]>(connectorsCacheKey) ?? []
	);
	// Ignores a response that lands after a newer request (another backbone or tenant).
	const connectorsRequestSeq = useRef(0);
	const [connectorsLoading, setConnectorsLoading] = useState(false);
	const [connectorsError, setConnectorsError] = useState<string | null>(null);
	const [summaryConnectorModal, setSummaryConnectorModal] =
		useState<BbConnectorListItem | null>(null);

	const [connectorsSummaryHoveredGroup, setConnectorsSummaryHoveredGroup] =
		useState<BackboneConnectorTypeGroup | 'all' | null>(null);
	const connectorsSummaryPopoverCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	const clearConnectorsSummaryPopoverTimer = useCallback(() => {
		if (connectorsSummaryPopoverCloseTimer.current != null) {
			clearTimeout(connectorsSummaryPopoverCloseTimer.current);
			connectorsSummaryPopoverCloseTimer.current = null;
		}
	}, []);

	const openConnectorsSummaryPopover = useCallback(
		(group: BackboneConnectorTypeGroup | 'all') => {
			clearConnectorsSummaryPopoverTimer();
			setConnectorsSummaryHoveredGroup(group);
		},
		[clearConnectorsSummaryPopoverTimer]
	);

	const scheduleCloseConnectorsSummaryPopover = useCallback(() => {
		clearConnectorsSummaryPopoverTimer();
		connectorsSummaryPopoverCloseTimer.current = setTimeout(() => {
			setConnectorsSummaryHoveredGroup(null);
			connectorsSummaryPopoverCloseTimer.current = null;
		}, 200);
	}, [clearConnectorsSummaryPopoverTimer]);

	/**
	 * BGP summary popover state. Tracks WHICH side of the "Up / Down" affordance
	 * is being hovered so the popover can list only that side's connectors.
	 * `null` means the popover is closed.
	 */
	const [bgpSummaryHoveredGroup, setBgpSummaryHoveredGroup] =
		useState<'up' | 'down' | null>(null);
	const bgpSummaryPopoverCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	const clearBgpSummaryPopoverTimer = useCallback(() => {
		if (bgpSummaryPopoverCloseTimer.current != null) {
			clearTimeout(bgpSummaryPopoverCloseTimer.current);
			bgpSummaryPopoverCloseTimer.current = null;
		}
	}, []);

	const openBgpSummaryPopover = useCallback(
		(group: 'up' | 'down') => {
			clearBgpSummaryPopoverTimer();
			setBgpSummaryHoveredGroup(group);
		},
		[clearBgpSummaryPopoverTimer]
	);

	const scheduleCloseBgpSummaryPopover = useCallback(() => {
		clearBgpSummaryPopoverTimer();
		bgpSummaryPopoverCloseTimer.current = setTimeout(() => {
			setBgpSummaryHoveredGroup(null);
			bgpSummaryPopoverCloseTimer.current = null;
		}, 200);
	}, [clearBgpSummaryPopoverTimer]);

	useEffect(
		() => () => {
			clearConnectorsSummaryPopoverTimer();
			clearBgpSummaryPopoverTimer();
		},
		[clearConnectorsSummaryPopoverTimer, clearBgpSummaryPopoverTimer]
	);

	const [bgpSessions, setBgpSessions] = useState<BackboneBgpSession[]>(
		() => readCachedFetch<BackboneBgpSession[]>(bgpSessionsCacheKey) ?? []
	);
	const [bgpLoading, setBgpLoading] = useState(false);
	const [bgpError, setBgpError] = useState<string | null>(null);
	const bgpRequestSeq = useRef(0);

	const loadConnectors = useCallback(async () => {
		if (tenantId == null) return;
		const seq = ++connectorsRequestSeq.current;
		// Only a first visit waits on a skeleton; a cached list stays up while this refreshes.
		// Nothing cached (another backbone or tenant): never keep the previous one's rows.
		const cached = readCachedFetch<BbConnectorListItem[]>(connectorsCacheKey);
		setConnectorList(cached ?? []);
		setConnectorsLoading(!cached);
		setConnectorsError(null);
		try {
			const response = await fetchBbConnectorList(api, tenantId, { limit: 100, offset: 0 });
			const body = response.data;
			const rows = body?.success && Array.isArray(body.data) ? body.data : [];
			writeCachedFetch(connectorsCacheKey, rows);
			if (seq === connectorsRequestSeq.current) setConnectorList(rows);
		} catch (err) {
			console.error('Connectors fetch failed', err);
			// Rows already on screen stay there; the next visit retries.
			if (seq === connectorsRequestSeq.current && !cached) {
				setConnectorsError(err instanceof Error ? err.message : 'Failed to load connectors');
				setConnectorList([]);
			}
		} finally {
			if (seq === connectorsRequestSeq.current) setConnectorsLoading(false);
		}
	}, [api, tenantId, connectorsCacheKey]);

	useEffect(() => {
		if (bb && tenantId != null) loadConnectors();
	}, [bb, tenantId, loadConnectors]);

	useEffect(() => {
		setEditedBackboneName(null);
		setIsEditingName(false);
		setBackboneMutationError(null);
		setBackboneTags([]);
		setIsEditingVrfFilter(false);
		setDraftVrfDisplayName('');
		setBgpSessions([]);
		setBgpError(null);
	}, [backboneId]);

	/** Hydrate tags from BBAAS (comma-separated string parsed in mapper to string[]). */
	useEffect(() => {
		if (bb == null || backboneId == null) return;
		if (String(bb.id) !== String(backboneId)) return;
		if (bb.tags != null && Array.isArray(bb.tags)) {
			setBackboneTags(bb.tags);
		} else {
			setBackboneTags([]);
		}
	}, [bb, backboneId]);

	const bbaasNumericId =
		backboneId != null ? Number.parseInt(backboneId, 10) : Number.NaN;
	const bbaasIdValid = Number.isFinite(bbaasNumericId) && bbaasNumericId > 0;

	const tab = searchParams.get('tab') || 'connectors';

	useEffect(() => {
		if (tab !== 'connectors') {
			setConnectorsSummaryHoveredGroup(null);
			setBgpSummaryHoveredGroup(null);
			clearConnectorsSummaryPopoverTimer();
			clearBgpSummaryPopoverTimer();
		}
	}, [tab, clearConnectorsSummaryPopoverTimer, clearBgpSummaryPopoverTimer]);

	const loadBgpSessions = useCallback(async () => {
		if (tenantId == null || !bbaasIdValid) return;
		const seq = ++bgpRequestSeq.current;
		// Same contract as loadConnectors: cached sessions stay up while this refreshes.
		const cached = readCachedFetch<BackboneBgpSession[]>(bgpSessionsCacheKey);
		setBgpSessions(cached ?? []);
		setBgpLoading(!cached);
		setBgpError(null);
		try {
			const response = await fetchBbaasBgpSessions(api, tenantId, bbaasNumericId, {
				limit: 500,
				offset: 0,
			});
			const body = response.data;
			const sessions =
				body?.success && Array.isArray(body.data)
					? body.data.map(mapBbaasBgpSessionToConnector)
					: [];
			writeCachedFetch(bgpSessionsCacheKey, sessions);
			if (seq === bgpRequestSeq.current) setBgpSessions(sessions);
		} catch (err) {
			console.error('BGP sessions fetch failed', err);
			if (seq === bgpRequestSeq.current && !cached) {
				setBgpError(err instanceof Error ? err.message : 'Failed to load BGP sessions');
				setBgpSessions([]);
			}
		} finally {
			if (seq === bgpRequestSeq.current) setBgpLoading(false);
		}
	}, [api, tenantId, bbaasNumericId, bbaasIdValid, bgpSessionsCacheKey]);

	useEffect(() => {
		if (tab !== 'bgp' || tenantId == null || !bbaasIdValid) return;
		void loadBgpSessions();
	}, [tab, tenantId, bbaasIdValid, loadBgpSessions, refresh]);

	const handleRetryBackbone = () => setRefresh((r) => r + 1);

	const saveBackboneName = async () => {
		if (tenantId == null || !bbaasIdValid) {
			setBackboneMutationError('Missing tenant or invalid backbone id');
			return;
		}
		const trimmed = draftBackboneName.trim();
		if (!trimmed) {
			setBackboneMutationError('Name cannot be empty');
			return;
		}
		setSavingBackboneName(true);
		setBackboneMutationError(null);
		try {
			const res = await updateBbaas(api, tenantId, bbaasNumericId, { name: trimmed });
			if (!res.data?.success) {
				throw new Error(res.data?.message ?? 'Update failed');
			}
			setEditedBackboneName(trimmed);
			setIsEditingName(false);
			setRefresh((r) => r + 1);
		} catch (e) {
			setBackboneMutationError(e instanceof Error ? e.message : 'Failed to update name');
		} finally {
			setSavingBackboneName(false);
		}
	};

	/**
	 * Persist tags from EditableTagList / TagManagementModal.
	 * PUT /kontroledge/bbaas/:id — body.tags is a comma-separated string per API.
	 */
	const saveBackboneTags = useCallback(
		async (tags: string[]) => {
			if (tenantId == null || !bbaasIdValid) {
				throw new Error('Missing tenant or invalid backbone id');
			}
			setSavingBackboneTags(true);
			setBackboneMutationError(null);
			try {
				const trimmed = tags.map((t) => t.trim()).filter(Boolean);
				const tagsStr = trimmed.join(', ');
				const res = await updateBbaas(api, tenantId, bbaasNumericId, {
					tags: tagsStr,
				});
				if (!res.data?.success) {
					throw new Error(res.data?.message ?? 'Update failed');
				}
				const returned = res.data.data?.tags;
				if (typeof returned === 'string') {
					setBackboneTags(
						returned.trim() === ''
							? []
							: returned.split(',').map((s) => s.trim()).filter(Boolean)
					);
				} else {
					setBackboneTags(trimmed);
				}
				setRefresh((r) => r + 1);
			} catch (e) {
				const msg = axios.isAxiosError(e)
					? (() => {
							const data = e.response?.data as { message?: string } | undefined;
							return data?.message ?? e.message ?? 'Failed to update tags';
						})()
					: e instanceof Error
						? e.message
						: 'Failed to update tags';
				setBackboneMutationError(msg);
				throw e;
			} finally {
				setSavingBackboneTags(false);
			}
		},
		[api, tenantId, bbaasIdValid, bbaasNumericId]
	);

	const dispatch = useAppDispatch();
	const vrfFilterSelection = useAppSelector(
		(state: RootState) => state.backboneDetails.vrfFilterSelection
	);
	const connectorTypeFilterSelection = useAppSelector(
		(state: RootState) => state.backboneDetails.connectorTypeFilterSelection
	);
	const bgpStatusFilterSelection = useAppSelector(
		(state: RootState) => state.backboneDetails.bgpStatusFilterSelection
	);

	/** VRF id when exactly one VRF is selected in the filter (rename applies to that VRF only). */
	const selectedVrfId = useMemo(() => {
		if (vrfFilterSelection.length !== 1) return null;
		const name = vrfFilterSelection[0];
		const row = connectorList.find((c) => c.vrf?.displayName === name);
		return row?.vrf?.id != null && Number.isFinite(row.vrf.id) ? row.vrf.id : null;
	}, [vrfFilterSelection, connectorList]);

	const routingVrfIds = useMemo(() => {
		const ids: number[] = [];
		for (const name of vrfFilterSelection) {
			const row = connectorList.find((c) => c.vrf?.displayName === name);
			if (row?.vrf?.id != null && Number.isFinite(row.vrf.id)) ids.push(row.vrf.id);
		}
		return ids;
	}, [vrfFilterSelection, connectorList]);

	useEffect(() => {
		setIsEditingVrfFilter(false);
		setDraftVrfDisplayName('');
	}, [vrfFilterSelection]);

	const saveVrfDisplayNameForFilter = async () => {
		if (tenantId == null || selectedVrfId == null) {
			setBackboneMutationError('Cannot save: missing tenant or VRF id.');
			return;
		}
		const trimmed = draftVrfDisplayName.trim();
		if (!trimmed) {
			setBackboneMutationError('VRF display name cannot be empty.');
			return;
		}
		setSavingVrfName(true);
		setBackboneMutationError(null);
		try {
			const res = await updateVrfDisplayName(api, tenantId, selectedVrfId, trimmed);
			if (!res.data?.success) {
				throw new Error(res.data?.message ?? 'Update failed');
			}
			const saved = res.data.data?.displayName?.trim() ?? trimmed;
			dispatch(setVrfFilterSelection([saved]));
			setIsEditingVrfFilter(false);
			await loadConnectors();
		} catch (e) {
			setBackboneMutationError(
				e instanceof Error ? e.message : 'Failed to update VRF display name'
			);
		} finally {
			setSavingVrfName(false);
		}
	};

	useEffect(() => {
		if (!backboneId) {
			navigate('/kontrol/backbone', { replace: true });
		}
	}, [backboneId, navigate]);

	// A visit starts unfiltered. Without a cache this happened as a side effect of the empty
	// first list below; a cached list skips that state, so reset explicitly on mount.
	useEffect(() => {
		dispatch(clearBackboneVrfs());
	}, [dispatch]);

	// Filter options: derived from connector list (VRF display names + connector type groups).
	useEffect(() => {
		if (connectorList.length > 0) {
			const vrfCounts: Record<string, number> = {};
			const displayNames = new Set<string>();
			const typeCounts: Record<string, number> = {};
			let bgpUp = 0;
			let bgpDown = 0;
			connectorList.forEach((c) => {
				const name = c.vrf?.displayName;
				if (name) {
					displayNames.add(name);
					vrfCounts[name] = (vrfCounts[name] ?? 0) + 1;
				}
				const group = connectedServiceTypeToGroup(c.connectedServiceType);
				if (group) {
					typeCounts[group] = (typeCounts[group] ?? 0) + 1;
				}
				if (c.bgpStatus?.isEstablished === true) bgpUp += 1;
				else bgpDown += 1;
			});
			dispatch(setBackboneVrfs({ vrfs: Array.from(displayNames), vrfCounts }));
			const presentTypes = BACKBONE_CONNECTOR_TYPE_GROUPS.filter(
				(g) => (typeCounts[g] ?? 0) > 0
			);
			dispatch(
				setBackboneConnectorTypes({
					connectorTypes: presentTypes,
					connectorTypeCounts: typeCounts,
				})
			);
			dispatch(setBackboneBgpStatusCounts({ up: bgpUp, down: bgpDown }));
		} else {
			dispatch(clearBackboneVrfs());
		}
	}, [connectorList, dispatch]);

	const connectorsForSummary = useMemo(() => {
		const vrfPass = isVrfFilterUnfiltered(vrfFilterSelection)
			? () => true
			: (c: BbConnectorListItem) =>
					c.vrf?.displayName != null && vrfFilterSelection.includes(c.vrf.displayName);
		const typePass =
			connectorTypeFilterSelection.length === 0
				? () => true
				: (c: BbConnectorListItem) =>
						matchesConnectorTypeGroupSelection(
							connectedServiceTypeToGroup(c.connectedServiceType),
							connectorTypeFilterSelection
						);
		const bgpPass =
			bgpStatusFilterSelection.length === 0
				? () => true
				: (c: BbConnectorListItem) =>
						matchesBgpStatusFilterSelection(
							connectorIsEstablishedToBgpStatusFilterId(c.bgpStatus?.isEstablished),
							bgpStatusFilterSelection
						);
		return connectorList.filter((c) => vrfPass(c) && typePass(c) && bgpPass(c));
	}, [
		connectorList,
		vrfFilterSelection,
		connectorTypeFilterSelection,
		bgpStatusFilterSelection,
	]);
	const backboneConnectorTotal = bb?.connectorsCount ?? connectorList.length;
	/** Outbound Mbps (same scope as VRF filter); matches Available Capacity bar in Connectors (GLOBAL_BW cap). */
	const totalOutboundBw = connectorsForSummary.reduce(
		(s, c) => s + (c.bandwidth?.outbound ?? 0),
		0
	);
	const connectorsBgpUpList = useMemo(
		() => connectorsForSummary.filter((c) => c.bgpStatus?.isEstablished === true),
		[connectorsForSummary]
	);
	const connectorsBgpDownList = useMemo(
		() => connectorsForSummary.filter((c) => c.bgpStatus?.isEstablished !== true),
		[connectorsForSummary]
	);
	const sessionsUp = connectorsBgpUpList.length;
	const sessionsDown = connectorsBgpDownList.length;

	const connectorTypeBreakdown = useMemo(() => {
		const counts: Record<string, number> = {};
		for (const c of connectorsForSummary) {
			const group = connectedServiceTypeToGroup(c.connectedServiceType);
			if (group != null) {
				counts[group] = (counts[group] ?? 0) + 1;
			}
		}
		return BACKBONE_CONNECTOR_TYPE_GROUPS
			.filter((g) => (counts[g] ?? 0) > 0)
			.map((group) => ({ group, count: counts[group] }));
	}, [connectorsForSummary]);

	if (!backboneId) return null;

	const summaryEmpty = !bb && !loading;
	/** Header + summary metrics (name, VRF, tags, KPI cards) only on Connectors Overview tab. */
	const hideBackboneOverviewCard = bb != null && !loading && tab !== 'connectors';

	return (
		<div className="p-4">
			{!hideBackboneOverviewCard && (
			<GenericCard
				isCollapsible={false}
				className="mt-2 mx-0"
				headerContent={
					loading ? (
						<div className="flex items-center gap-2">
							<SkeletonBlock h={24} w={180} className="rounded" />
							<SkeletonBlock h={22} w={70} className="rounded-full" />
						</div>
					) : bb ? (
						<div className="flex flex-col gap-1 w-full min-w-0">
							{backboneMutationError != null && (
								<p className="text-xs text-semantic-text-error" role="alert">
									{backboneMutationError}
								</p>
							)}
							<div className="flex items-center justify-between gap-4 w-full flex-wrap">
								<div className="flex min-w-0 flex-1 flex-col gap-1.5">
									<h2 className="flex flex-wrap items-center gap-2 text-md font-bold leading-none text-semantic-text-primary">
										<span className="flex shrink-0 items-center gap-2 text-sm font-semibold leading-none text-semantic-text-secondary">
											<span className="text-xs font-medium leading-none tracking-wide text-semantic-text-tertiary">
												Backbone Name
											</span>
										</span>
										{isEditingName ? (
											<div
												className="relative min-w-[120px] max-w-[280px] self-center"
												aria-busy={savingBackboneName}
											>
												<input
													type="text"
													value={draftBackboneName}
													onChange={(e) => setDraftBackboneName(e.target.value)}
													disabled={savingBackboneName}
													className={`w-full rounded border border-semantic-border-primary bg-semantic-bg-primary py-1 text-sm font-bold text-semantic-text-primary disabled:opacity-60 ${savingBackboneName ? 'pl-2 pr-9' : 'px-2'
														}`}
												/>
												{savingBackboneName && <BackboneNameInputInlineLoader />}
											</div>
										) : (
											<button
												type="button"
												onClick={() => {
													if (isEditingVrfFilter || savingVrfName) return;
													if (tenantId == null || !bbaasIdValid) return;
													setIsEditingName(true);
													setDraftBackboneName(editedBackboneName ?? bb.name ?? '');
													setBackboneMutationError(null);
												}}
												disabled={
													tenantId == null ||
													!bbaasIdValid ||
													isEditingVrfFilter ||
													savingVrfName
												}
												className="cursor-pointer self-center rounded px-0.5 py-0.5 text-left text-md font-bold leading-none text-semantic-text-primary hover:bg-semantic-bg-tertiary hover:underline disabled:cursor-not-allowed disabled:no-underline disabled:opacity-50"
												title="Edit backbone name"
											>
												{editedBackboneName ?? bb.name}
											</button>
										)}
										{vrfFilterSelection.length === 1 && (
											<span className="flex flex-wrap items-center gap-2 text-sm font-semibold leading-none text-semantic-text-secondary">
												<span aria-hidden className="leading-none text-semantic-text-tertiary">
													·
												</span>
												<span className="text-xs font-medium leading-none tracking-wide text-semantic-text-tertiary">
													VRF Name
												</span>
												{isEditingVrfFilter ? (
													<span className="flex items-center gap-1">
														<div
															className="relative min-w-[100px] max-w-[220px]"
															aria-busy={savingVrfName}
														>
															<input
																type="text"
																value={draftVrfDisplayName}
																onChange={(e) => setDraftVrfDisplayName(e.target.value)}
																disabled={savingVrfName}
																className={`w-full rounded border border-semantic-border-primary bg-semantic-bg-primary py-1 text-sm font-bold text-semantic-text-primary disabled:opacity-60 ${savingVrfName ? 'pl-2 pr-9' : 'px-2'
																	}`}
															/>
															{savingVrfName && <VrfHeaderInputInlineLoader />}
														</div>
														<button
															type="button"
															onClick={() => void saveVrfDisplayNameForFilter()}
															disabled={
																savingVrfName ||
																tenantId == null ||
																selectedVrfId == null
															}
															className="p-1 rounded hover:bg-semantic-bg-tertiary text-semantic-text-secondary hover:text-semantic-text-success transition-colors disabled:opacity-40 disabled:pointer-events-none"
															aria-label="Save VRF name"
														>
															<IconCheck className="w-5 h-5" />
														</button>
														<button
															type="button"
															onClick={() => {
																setDraftVrfDisplayName(vrfFilterSelection[0] ?? '');
																setIsEditingVrfFilter(false);
																setBackboneMutationError(null);
															}}
															disabled={savingVrfName}
															className="p-1 rounded hover:bg-semantic-bg-tertiary text-semantic-text-secondary hover:text-semantic-text-primary transition-colors disabled:opacity-40 disabled:pointer-events-none"
															aria-label="Cancel VRF edit"
														>
															<IconCancel className="w-5 h-5" />
														</button>
													</span>
												) : (
													<button
														type="button"
														onClick={() => {
															if (isEditingName || savingBackboneName) return;
															if (selectedVrfId == null || tenantId == null) return;
															setIsEditingName(false);
															setDraftVrfDisplayName(vrfFilterSelection[0] ?? '');
															setIsEditingVrfFilter(true);
															setBackboneMutationError(null);
														}}
														disabled={
															isEditingName ||
															savingBackboneName ||
															savingVrfName ||
															tenantId == null ||
															selectedVrfId == null
														}
														className="cursor-pointer rounded px-1 py-0.5 text-sm font-bold leading-none text-semantic-text-primary hover:bg-semantic-bg-tertiary hover:underline disabled:cursor-not-allowed disabled:no-underline disabled:opacity-50"
														title={
															selectedVrfId == null
																? 'Load connectors to edit this VRF name'
																: 'Edit VRF display name'
														}
													>
														{vrfFilterSelection[0]}
													</button>
												)}
											</span>
										)}
										{isEditingName && (
											<>
												<button
													type="button"
													onClick={() => void saveBackboneName()}
													disabled={
														savingBackboneName ||
														tenantId == null ||
														!bbaasIdValid ||
														isEditingVrfFilter ||
														savingVrfName
													}
													className="p-1 self-center rounded hover:bg-semantic-bg-tertiary text-semantic-text-secondary hover:text-semantic-text-success transition-colors disabled:opacity-40 disabled:pointer-events-none"
													aria-label="Save"
												>
													<IconCheck className="w-5 h-5" />
												</button>
												<button
													type="button"
													onClick={() => {
														setDraftBackboneName(editedBackboneName ?? bb.name ?? '');
														setIsEditingName(false);
														setBackboneMutationError(null);
													}}
													disabled={savingBackboneName || savingVrfName}
													className="p-1 self-center rounded hover:bg-semantic-bg-tertiary text-semantic-text-secondary hover:text-semantic-text-primary transition-colors disabled:opacity-40 disabled:pointer-events-none"
													aria-label="Cancel"
												>
													<IconCancel className="w-5 h-5" />
												</button>
											</>
										)}
									</h2>
									<div className="flex items-center">
										<GenericTag
											label={bb.status}
											variant={
												bb.status === 'Active'
													? 'success'
													: bb.status === 'Inactive'
														? 'error'
														: 'neutral'
											}
											size="small"
										/>
									</div>
								</div>
								<div className="flex min-w-0 flex-1 justify-end max-w-[min(100%,20rem)]">
									<EditableTagList
										tags={backboneTags}
										onUpdate={saveBackboneTags}
										disabled={
											savingBackboneTags ||
											savingBackboneName ||
											savingVrfName ||
											isEditingVrfFilter ||
											tenantId == null ||
											!bbaasIdValid
										}
										isLoading={savingBackboneTags}
										// Backbone services live in `KontrolEdgeBBAAS` on the
										// backend; the Tags pivot keys them as `KE_BBAAS` —
										// the only Backbone-related entry in the resource
										// type ENUM per TAGS-API-CONTRACT.md §2.3.
										resourceType="ke-bbaas"
										resourceId={bbaasNumericId}
										tenantId={resourceTenantId}
										initialAssignedTags={initialAssignedTags}
									/>
								</div>
							</div>
						</div>
					) : (
						<h2 className="text-md font-bold text-semantic-text-primary">Backbone</h2>
					)
				}
			bodyContent={tab === 'topology' ? null :
				<DataStateView
					loading={loading}
					error={backboneError}
					isEmpty={summaryEmpty}
					onRetry={handleRetryBackbone}
					loadingContent={
						<div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
							{Array.from({ length: 3 }).map((_, i) => (
								<SkeletonBlock key={i} h={72} className="rounded-md w-full" />
							))}
						</div>
					}
					errorTitle="Failed to load backbone"
					emptyTitle="Backbone not found"
					emptyDescription="We couldn't load this backbone. It may have been removed or you may not have access."
				>
					{bb ? (
						<div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-6">
								{/* Bandwidth card: Committed (top) + Current (bottom). */}
								<GenericCard className="lg:col-span-2" isCollapsible={false} variant="primary" bodyContent={
									<div className="flex justify-between gap-2">
										<div className="flex flex-col">
											<span className="text-xs text-semantic-text-secondary">
												Committed Bandwidth
											</span>
											<span className="text-sm font-medium text-semantic-text-brand">
												{fmtBw(roundBw(bb.capacity ?? GLOBAL_BW))}
											</span>
										</div>
										<div className="flex flex-col">
											<span className="text-xs text-semantic-text-secondary">
												Current Bandwidth
											</span>
											<div className="mt-0.5 flex flex-wrap items-baseline gap-x-1.5 gap-y-0">
												<span className="text-sm font-medium text-semantic-text-brand tabular-nums">
													{formatBandwidthFromMbpsInline(roundBw(totalOutboundBw))}
												</span>
												<span className="text-xs text-semantic-text-secondary">
													of {fmtBw(GLOBAL_BW)}
												</span>
											</div>
										</div>
									</div>
								} />
								<GenericCard className="lg:col-span-3" style={{ cursor: 'pointer' }} isCollapsible={false} variant="primary" bodyContent={
									<>
										<span className="text-xs text-semantic-text-secondary">
											Connectors
										</span>
										<div className="relative mt-0.5 max-w-full">
											{/* While the connector fetch is in flight the
											    `connectorsCount` from the backbone metadata is
											    still showing as if it were a final value (and the
											    type breakdown is empty). Render a "—" placeholder
											    so the user doesn't see a stale "6" before the
											    real list arrives. */}
											{connectorsLoading ? (
												<span className="text-sm font-medium text-semantic-text-secondary">
													{'\u2014'}
												</span>
											) : connectorTypeBreakdown.length === 0 ? (
												<span
													onMouseEnter={() => openConnectorsSummaryPopover('all')}
													onMouseLeave={scheduleCloseConnectorsSummaryPopover}
													className={`text-sm font-medium text-semantic-text-secondary ${
														connectorsForSummary.length > 0
															? 'cursor-default'
															: ''
													}`}
												>
													{isVrfFilterUnfiltered(vrfFilterSelection)
														? backboneConnectorTotal
														: `${connectorsForSummary.length} / ${backboneConnectorTotal}`}
												</span>
											) : (
												<div className="flex items-baseline gap-3">
													{connectorTypeBreakdown.map(({ group, count }) => {
														const isActive =
															connectorsSummaryHoveredGroup === group;
														const popoverConnectors = isActive
															? connectorsForSummary.filter(
																	(c) =>
																		connectedServiceTypeToGroup(
																			c.connectedServiceType
																		) === group
																)
															: [];
														return (
															<div
																key={group}
																onMouseEnter={() =>
																	openConnectorsSummaryPopover(group)
																}
																onMouseLeave={scheduleCloseConnectorsSummaryPopover}
																className="relative flex-1 min-w-0"
															>
																<span className="inline-flex w-full cursor-pointer items-baseline gap-1 truncate text-sm font-medium text-semantic-text-secondary hover:text-semantic-text-brand">
																	<span className="tabular-nums">{count}</span>
																	<span className="truncate">{group}</span>
																</span>
																{isActive && popoverConnectors.length > 0 && (
																	<div
																		role="listbox"
																		aria-label={`${group} connector subscription IDs`}
																		className="absolute left-0 top-full z-50 mt-1 max-h-56 min-w-[12rem] max-w-sm overflow-hidden rounded-md border border-semantic-border-primary bg-semantic-bg-primary py-1.5 pl-2 pr-1 shadow-lg"
																		onMouseEnter={clearConnectorsSummaryPopoverTimer}
																		onMouseLeave={scheduleCloseConnectorsSummaryPopover}
																	>
																		<p className="mb-1 px-1.5 text-[10px] font-medium uppercase tracking-wide text-semantic-text-secondary">
																			{group}
																		</p>
																		<ul className="max-h-44 space-y-0.5 overflow-y-auto pr-1">
																			{popoverConnectors.map((c) => (
																				<li key={c.id}>
																					<button
																						type="button"
																						className="w-full rounded px-1.5 py-1 text-left font-mono text-xs text-semantic-text-brand hover:bg-semantic-bg-tertiary"
																						onClick={() => {
																							setSummaryConnectorModal(c);
																							setConnectorsSummaryHoveredGroup(null);
																							clearConnectorsSummaryPopoverTimer();
																						}}
																					>
																						{connectorSummaryListLabel(c)}
																					</button>
																				</li>
																			))}
																		</ul>
																	</div>
																)}
															</div>
														);
													})}
												</div>
											)}
											{connectorsSummaryHoveredGroup === 'all' &&
												connectorsForSummary.length > 0 && (
													<div
														role="listbox"
														aria-label="Connector subscription IDs"
														className="absolute left-0 top-full z-50 mt-1 max-h-56 min-w-[12rem] max-w-sm overflow-hidden rounded-md border border-semantic-border-primary bg-semantic-bg-primary py-1.5 pl-2 pr-1 shadow-lg"
														onMouseEnter={clearConnectorsSummaryPopoverTimer}
														onMouseLeave={scheduleCloseConnectorsSummaryPopover}
													>
														<ul className="max-h-44 space-y-0.5 overflow-y-auto pr-1">
															{connectorsForSummary.map((c) => (
																<li key={c.id}>
																	<button
																		type="button"
																		className="w-full rounded px-1.5 py-1 text-left font-mono text-xs text-semantic-text-brand hover:bg-semantic-bg-tertiary"
																		onClick={() => {
																			setSummaryConnectorModal(c);
																			setConnectorsSummaryHoveredGroup(null);
																			clearConnectorsSummaryPopoverTimer();
																		}}
																	>
																		{connectorSummaryListLabel(c)}
																	</button>
																</li>
															))}
														</ul>
													</div>
												)}
										</div>
									</>
								} />
								<GenericCard className="lg:col-span-1" style={{ cursor: 'pointer' }} isCollapsible={false} variant="primary" bodyContent={
									<>
										<span className="text-xs text-semantic-text-secondary">
											BGP Status
										</span>
										<div className="relative mt-0.5 max-w-full">
											{/* During the connector fetch the up/down counts are
											    still 0 (no items in the list yet); render "—"
											    instead of "0 Up / 0 Down" until the real data
											    arrives. */}
											{connectorsLoading ? (
												<span className="text-sm font-medium text-semantic-text-secondary">
													{'\u2014'}
												</span>
											) : (
												/* Up / Down each have their own hover target so the
												    popover filters to just that side's connectors. */
												<span className="inline-flex items-baseline gap-1 text-sm font-medium tabular-nums">
													<span
														onMouseEnter={() => openBgpSummaryPopover('up')}
														onMouseLeave={scheduleCloseBgpSummaryPopover}
														className="inline-flex items-baseline gap-1 cursor-pointer text-semantic-text-brand hover:text-semantic-text-success"
													>
														<span className="tabular-nums">{sessionsUp}</span>
														<span className="font-normal text-semantic-text-secondary">Up</span>
													</span>
													<span className="text-xs font-normal text-semantic-text-secondary" aria-hidden>
														/
													</span>
													<span
														onMouseEnter={() => openBgpSummaryPopover('down')}
														onMouseLeave={scheduleCloseBgpSummaryPopover}
														className="inline-flex items-baseline gap-1 cursor-pointer text-semantic-text-brand hover:text-semantic-text-danger"
													>
														<span className="tabular-nums">{sessionsDown}</span>
														<span className="font-normal text-semantic-text-secondary">Down</span>
													</span>
												</span>
											)}
											{bgpSummaryHoveredGroup != null &&
												(() => {
													const popoverList =
														bgpSummaryHoveredGroup === 'up'
															? connectorsBgpUpList
															: connectorsBgpDownList;
													const titleLabel =
														bgpSummaryHoveredGroup === 'up' ? 'Up' : 'Down';
													return (
														<div
															role="listbox"
															aria-label={`Connectors with BGP ${titleLabel}`}
															className="absolute left-0 top-full z-50 mt-1 max-h-64 min-w-[14rem] max-w-sm overflow-hidden rounded-md border border-semantic-border-primary bg-semantic-bg-primary py-1.5 pl-2 pr-1 shadow-lg"
															onMouseEnter={clearBgpSummaryPopoverTimer}
															onMouseLeave={scheduleCloseBgpSummaryPopover}
														>
															<p className="mb-1 pb-1.5 pr-1 px-1.5 text-[10px] font-medium uppercase tracking-wide text-semantic-text-secondary border-b border-semantic-border-primary">
																{titleLabel} ({popoverList.length})
															</p>
															{popoverList.length === 0 ? (
																<p className="px-1.5 py-1 text-xs text-semantic-text-tertiary">
																	None
																</p>
															) : (
																<ul className="max-h-44 space-y-0.5 overflow-y-auto pr-1">
																	{popoverList.map((c) => (
																		<li key={c.id}>
																			<button
																				type="button"
																				className="w-full rounded px-1.5 py-1 text-left font-mono text-xs text-semantic-text-brand hover:bg-semantic-bg-tertiary"
																				onClick={() => {
																					setSummaryConnectorModal(c);
																					setBgpSummaryHoveredGroup(null);
																					clearBgpSummaryPopoverTimer();
																				}}
																			>
																				{connectorSummaryListLabel(c)}
																			</button>
																		</li>
																	))}
																</ul>
															)}
														</div>
													);
												})()}
										</div>
									</>
								} />
							</div>
						) : null}
					</DataStateView>
				}
			/>
			)}

			{bb && (
				<div className="mt-4">
					{tab === 'connectors' && (
						<ConnectorsTab
							connectors={connectorList}
							vrfFilterSelection={vrfFilterSelection}
							connectorTypeFilterSelection={connectorTypeFilterSelection}
							bgpStatusFilterSelection={bgpStatusFilterSelection}
							loading={connectorsLoading || loading}
							error={connectorsError ?? backboneError}
							onRetry={loadConnectors}
						/>
					)}
					{tab === 'topology' && (
						<TopologyTab
							connectors={connectorList}
							vrfFilterSelection={vrfFilterSelection}
							connectorTypeFilterSelection={connectorTypeFilterSelection}
							bgpStatusFilterSelection={bgpStatusFilterSelection}
							tenantId={tenantId}
							backboneName={editedBackboneName ?? bb?.name ?? undefined}
							loading={connectorsLoading || loading}
							error={connectorsError ?? backboneError}
							onRetry={loadConnectors}
						/>
					)}
					{tab === 'bgp' && (
						<BGPTab
							vrfFilterSelection={vrfFilterSelection}
							connectorTypeFilterSelection={connectorTypeFilterSelection}
							bgpStatusFilterSelection={bgpStatusFilterSelection}
							sessions={bgpSessions}
							loading={bgpLoading}
							error={bgpError}
							onRetry={() => {
								void loadBgpSessions();
							}}
							api={api}
							tenantId={tenantId}
						/>
					)}
					{tab === 'routing' && (
						<RoutingTab
							vrfFilterSelection={vrfFilterSelection}
							connectorTypeFilterSelection={connectorTypeFilterSelection}
							routingVrfIds={routingVrfIds}
							tenantId={tenantId}
							bbaasId={bbaasNumericId}
							bbaasIdValid={bbaasIdValid}
							connectorsLoading={connectorsLoading}
						/>
					)}
					{tab === 'policy' && <PolicyRulesTab vrfFilterSelection={vrfFilterSelection} />}
				</div>
			)}

			<ConnectorsModal
				connector={summaryConnectorModal}
				onClose={() => setSummaryConnectorModal(null)}
				tenantId={tenantId}
			/>
		</div>
	);
};

export default BackboneDetails;
